/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useReactToPrint } from 'react-to-print';
import {
  format,
  isWithinInterval,
  subDays,
  startOfDay,
  endOfDay,
  parseISO,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Tooltip } from 'react-tooltip';

import { BoxShadow } from '../../../../shared/GlobalStyles';
import Button from '../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import DropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';
import {
  fetchAthletes,
  fetchVBTData,
  setDuplicateVBTDashModal,
  setEndDateData,
  setGroup,
  setSaveVBTDashModal,
  setSelectedAthlete,
  setSelectedEndDateAnalysis,
  setSelectedEndDateOverview,
  setSelectedExercise,
  setShareVBTDashModal,
  setStartDate,
} from '../../ducks/vbtDashboardActions';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import VBTAnalysisTableNoData from './VBTAnalysisTableNoData';
import Text from '../../../../shared/_TBComponents/Text';
import Icon from '../../../../shared/_TBComponents/Icon';
import ThresholdFilter from '../ThresholdFilter';
import VBTAnalysisTable from './VBTAnalysisTable';

const ColorNote = styled('div')`
  color: #444444;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: normal;
`;

const DashboardContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 10px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  height: fit-content;
  margin: 15px 15px 7px 15px;
  padding: 16px;
  position: relative;

  @media print {
    margin: 0px;
  }
`;

const IconContainer = styled('div')`
  display: flex;
  margin: 5px 0;
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const ModalMessage = styled('div')`
  font-weight: 300;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const NoDataModal = styled('div')`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  color: #424242;
  font-family: 'Nunito Sans';
  font-size: 19px;
  height: 119px;
  left: 50%;
  margin: -45px 0px 0px -182px;
  padding: 15px;
  position: absolute;
  text-align: center;
  top: 50%;
  width: 400px;
`;

const OptionsHeader = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 10px;

  @media print {
    display: none;
  }
`;

const SpinnerContainer = styled('div')`
  height: 550px;
`;

const SubtitleContainer = styled('div')`
  display: flex;
`;

const AnalysisComponent = () => {
  const activeTemplate = useSelector((state) => state.vbtDashboard.data.activeTemplate);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const dateRange = useSelector((state) => state.vbtDashboard.data.dateRange);
  const endDateData = useSelector((state) => state.vbtDashboard.data.endDateData);
  const exercises = useSelector((state) => state.vbtDashboard.data.exercises);
  const group = useSelector((state) => state.vbtDashboard.data.group);
  const groups = useSelector((state) => state.vbtDashboard.data.groups);
  const isExerciseSelected = useSelector((state) => state.vbtDashboard.ui.isExerciseSelected);
  const isVBTDataLoading = useSelector((state) => state.vbtDashboard.ui.isVBTDataLoading);
  const selectedExercise = useSelector((state) => state.vbtDashboard.data.selectedExercise);
  const selectedEndDateAnalysis = useSelector(
    (state) => state.vbtDashboard.data.selectedEndDateAnalysis,
  );
  const startDate = useSelector((state) => state.vbtDashboard.data.startDate);
  const tableThreshold = useSelector((state) => state.vbtDashboard.data.tableThreshold);
  const timeZone = useSelector((state) => state.auth.data.currentUser.userSettings.timeZone);
  const vbtData = useSelector((state) => state.vbtDashboard.data.vbtData);

  const [readyPrint, setReadyPrint] = useState(false);

  const dispatch = useDispatch();
  const printRef = useRef();

  const datesWithData = vbtData?.tableData?.dates.map((item) => parseISO(item.date));
  const groupData = vbtData?.tableData?.dates.find(
    (day) => day.date === format(new Date(selectedEndDateAnalysis), 'yyyy-MM-dd'),
  )?.groupData;
  const tableData = vbtData?.tableData?.dates.find(
    (day) => day.date === format(new Date(selectedEndDateAnalysis), 'yyyy-MM-dd'),
  )?.athleteData;

  const hasGroupAndExer = group?.id && isExerciseSelected;
  const hasTableData = tableData?.length > 0;

  // Helper to normalize a date to the user's timezone and strip time
  const getZonedDate = (date) => startOfDay(toZonedTime(date, timeZone));

  useEffect(() => {
    if (!group?.id || !isExerciseSelected) return;
    dispatch(
      fetchVBTData(
        currentUser.accountCode,
        endDateData,
        selectedExercise?.id,
        group?.id,
        startDate,
      ),
    );
  }, [endDateData, group?.id, selectedExercise, startDate]);

  const handleDuplicate = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Duplicate', 'Duplicate button clicked');
    dispatch(setDuplicateVBTDashModal(true));
  };

  const handleEndDate = (date) => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Date Picker', 'End date selected');
    // Convert picked date to user's timezone and strip time
    // Only update start date and overview date to refetch data if date is outside current range
    const pickedDate = getZonedDate(date);
    const interval = {
      start: startOfDay(toZonedTime(startDate, timeZone)),
      end: endOfDay(toZonedTime(endDateData, timeZone)),
    };
    if (isWithinInterval(pickedDate, interval)) {
      dispatch(setSelectedEndDateAnalysis(pickedDate));
    } else {
      batch(() => {
        dispatch(setEndDateData(format(pickedDate, 'yyyy-MM-dd')));
        dispatch(setSelectedEndDateAnalysis(pickedDate));
        dispatch(setSelectedEndDateOverview(pickedDate));
        dispatch(setStartDate(format(subDays(pickedDate, dateRange), 'yyyy-MM-dd')));
      });
    }
  };

  const handlePrint = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Print', 'Print button clicked');
    setReadyPrint(true);
  };

  const handleReactPrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      // Determine the num of columns to handle setting zoom
      const cols = groupData?.sets?.length;
      let zoom = 'unset';

      switch (cols) {
        case 10:
          zoom = '33%';
          break;
        case 9:
          zoom = '36%';
          break;
        case 8:
          zoom = '41%';
          break;
        case 7:
          zoom = '46%';
          break;
        case 6:
          zoom = '53%';
          break;
        case 5:
          zoom = '61%';
          break;
        case 4:
          zoom = '75%';
          break;
        case 3:
          zoom = '95%';
          break;
        default:
          break;
      }
      const style = document.createElement('style');
      style.textContent = `
        @page {
          size: A4 landscape;
          margin: 2mm;
        }
        body {
          -webkit-print-color-adjust: exact;
          overflow: visible !important;
        }
        .noHoverCategoryColumnTitleCell, noHoverCategoryColumnTitleCellFirst, .rawValueTitle {
          position: unset !important;
        }
        table {
          page-break-before: avoid;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        #vbtAnalysisTable {
          zoom: ${zoom};
        }
      `;
      document.head.appendChild(style);
    },
    onAfterPrint: () => {
      // Amplitude tracking
      dashboardTracker('VBT Dashboard', 'Analysis - Print', 'Printed');
      document.head.removeChild(document.head.lastChild);
      setReadyPrint(false);
    },
  });

  const handleSave = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Save', 'Save button clicked');
    dispatch(setSaveVBTDashModal(true));
  };

  const handleShare = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Share', 'Share button clicked');
    dispatch(setShareVBTDashModal(true));
  };

  const setExercise = (exer) => {
    if (!exer.id) return;
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Exercise Picker', 'Exercise selected');
    dispatch(setSelectedExercise(true, exer));
  };

  const setSelectedGroup = (option) => {
    if (!option.id) return;
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Group Picker', 'Group selected');
    batch(() => {
      dispatch(setGroup(option));
      dispatch(setSelectedAthlete(null, false));
      dispatch(fetchAthletes(currentUser.accountCode, option.id));
    });
  };

  useEffect(() => {
    if (readyPrint) {
      setTimeout(() => {
        handleReactPrint();
      }, 1500);
    }
  }, [readyPrint]);

  return (
    <>
      <DashboardContainer ref={printRef}>
        <SubtitleContainer>
          <Icon
            color='#9E9E9E'
            icon='group'
            size='24px'
          />
          <Text
            styledTextProps={{
              fontSize: '19px',
              fontWeight: '700',
              marginLeft: '10px',
            }}
          >
            Group Analysis
          </Text>
        </SubtitleContainer>
        <OptionsHeader>
          <MenuOptionsContainer>
            <DropDown
              defaultOption={group || {}}
              label='name'
              list={groups}
              headerWidthPx={220}
              placeholder='Select a Group'
              selectItemFunction={setSelectedGroup}
            />
            <DropDown
              defaultOption={selectedExercise || {}}
              disabled={!group?.id}
              label='name'
              list={exercises}
              headerWidthPx={220}
              placeholder='Select an Exercise'
              selectItemFunction={setExercise}
            />
            <ThresholdFilter />
          </MenuOptionsContainer>
          <MenuOptionsContainer>
            <DatePicker
              datesWithData={datesWithData}
              setStartDate={handleEndDate}
              startDate={selectedEndDateAnalysis ? new Date(selectedEndDateAnalysis) : null}
              styledWrapperProps={{
                alignSelf: 'center',
                customBorder: '1px solid #D8D8D8',
                customHeight: '42px',
              }}
            />
            <IconContainer>
              {activeTemplate ? (
                <>
                  <span
                    data-tooltip-content='Duplicate Dashboard'
                    data-tooltip-id='duplicate-tooltip'
                  >
                    <Button
                      customBorderRadius='4px'
                      iconOnly
                      iconSize={16}
                      icon='duplicate'
                      onClick={handleDuplicate}
                      styledWrapperProps={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #D8D8D8',
                        marginLeft: '8px',
                        padding: '11px',
                      }}
                    />
                  </span>
                  <Tooltip
                    id='duplicate-tooltip'
                    place='bottom'
                    style={{
                      fontFamily: 'Nunito Sans',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                    }}
                  />
                </>
              ) : (
                <>
                  <span
                    data-tooltip-content='Save Dashboard'
                    data-tooltip-id='save-tooltip'
                  >
                    <Button
                      customBorderRadius='4px'
                      iconOnly
                      iconSize={16}
                      icon='save2'
                      onClick={handleSave}
                      styledWrapperProps={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #D8D8D8',
                        marginLeft: '8px',
                        padding: '11px',
                      }}
                    />
                  </span>
                  <Tooltip
                    id='save-tooltip'
                    place='bottom'
                    style={{
                      fontFamily: 'Nunito Sans',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                    }}
                  />
                </>
              )}
              <>
                <span
                  data-tooltip-content='Share Dashboard'
                  data-tooltip-id='share-tooltip'
                >
                  <Button
                    customBorderRadius='4px'
                    disabled={!hasTableData}
                    iconOnly
                    iconSize={16}
                    icon='share-ams'
                    onClick={handleShare}
                    styledWrapperProps={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #D8D8D8',
                      marginLeft: '8px',
                      padding: '11px',
                    }}
                  />
                </span>
                <Tooltip
                  id='share-tooltip'
                  hidden={!hasTableData}
                  place='bottom'
                  style={{
                    fontFamily: 'Nunito Sans',
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                  }}
                />
              </>
              <>
                <span
                  data-tooltip-content='Print Dashboard'
                  data-tooltip-id='print-tooltip'
                >
                  <Button
                    customBorderRadius='4px'
                    disabled={!hasTableData}
                    iconOnly
                    iconSize={16}
                    icon='printer'
                    onClick={handlePrint}
                    styledWrapperProps={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #D8D8D8',
                      marginLeft: '8px',
                      padding: '11px',
                    }}
                  />
                </span>
                <Tooltip
                  id='print-tooltip'
                  hidden={!hasTableData}
                  place='bottom'
                  style={{
                    fontFamily: 'Nunito Sans',
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                  }}
                />
              </>
            </IconContainer>
          </MenuOptionsContainer>
        </OptionsHeader>
        <ColorNote>
          <span style={{ fontWeight: 'bold' }}>NOTE: </span>
          The column background color is determined by the threshold set.
          The column will be red if the athlete is below the threshold and
          green if the athlete is within the threshold.
        </ColorNote>
        {isVBTDataLoading ? (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        ) : !hasTableData ? (
          <>
            <VBTAnalysisTableNoData
              dataRows={['', '', '', '', '']}
            />
            <NoDataModal>
              <ModalTitle>
                {!hasGroupAndExer && !hasTableData ? 'Select a Group and Exercise' : 'No Data Available'}
              </ModalTitle>
              <ModalMessage>
                {!hasGroupAndExer && !hasTableData
                  ? 'Select a group and exercise using the dropdowns above to get started'
                  : 'There is no data available, please adjust your selections above'}
              </ModalMessage>
            </NoDataModal>
          </>
        ) : (
          <VBTAnalysisTable
            dataRows={tableData}
            groupData={groupData}
            readyPrint={readyPrint}
            threshold={tableThreshold}
          />
        )}
      </DashboardContainer>
    </>
  );
};

export default AnalysisComponent;
